import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Lock, MapPin, Shield, Camera, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import AuroraSection, { Aurora } from "@/components/motion/Aurora";
import RevealTitle from "@/components/motion/RevealTitle";

const HERO =
  "https://images.unsplash.com/photo-1601285462028-111ed137177e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHx2b2xrc3dhZ2VuJTIwY2FtcGVyJTIwdmFuJTIwbmF0dXJlfGVufDB8fHx8MTc3ODQyMjU1Mnww&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const enterCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    try {
      await api.get(`/r/${code.trim().toUpperCase()}`);
      navigate(`/reserva/${code.trim().toUpperCase()}`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="aurora-page relative isolate min-h-screen bg-ivory">
      <Aurora variant="light" fixed />
      {/* Hero */}
      <section className="relative h-[100svh] min-h-[640px] overflow-hidden">
        <img
          src={HERO}
          alt="Volkswagen camper en montaña"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-forest-deep/80" />
        <div className="absolute inset-0 grain opacity-50 mix-blend-overlay" />

        <nav className="relative z-20 container-x py-6 flex items-center justify-between text-white">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-full bg-terracotta flex items-center justify-center font-display font-bold">
              V
            </div>
            <span className="font-display text-xl font-bold tracking-tight">
              VanCheck
            </span>
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate("/admin/login")}
            className="text-white hover:bg-white/10"
            data-testid="landing-owner-login-btn"
          >
            <Lock className="h-4 w-4 mr-2" /> Acceso propietario
          </Button>
        </nav>

        <div className="relative z-10 container-x flex items-center min-h-[calc(100svh-96px)]">
          <div className="max-w-3xl text-white">
            <span className="text-overline text-warn">
              Volkswagen California Ocean · Yescapa #91679
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mt-4 mb-6 leading-[0.95]">
              Tu aventura empieza con
              <br />
              <span className="text-warn">un solo código.</span>
            </h1>
            <p className="text-lg text-white/85 max-w-xl leading-relaxed mb-8">
              Realiza el check-in y check-out de la furgoneta de forma
              transparente: estado del vehículo, inventario, firma y fianza.
              Todo desde tu móvil.
            </p>

            <form
              onSubmit={enterCode}
              className="bg-white/95 backdrop-blur p-2 rounded-2xl shadow-xl flex flex-col sm:flex-row gap-2 max-w-xl"
              data-testid="landing-code-form"
            >
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="CÓDIGO DE RESERVA (ej. AB7K3M9P)"
                className="flex-1 border-0 bg-transparent text-charcoal text-base font-mono tracking-wider placeholder:text-warmgray-light focus-visible:ring-0"
                data-testid="landing-code-input"
                maxLength={12}
              />
              <Button
                type="submit"
                disabled={loading}
                className="bg-terracotta hover:bg-terracotta-dark text-white px-6 h-12 rounded-xl"
                data-testid="landing-code-submit"
              >
                {loading ? "Buscando..." : "Acceder"}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Features */}
      <AuroraSection variant="light" className="py-20 sm:py-28">
        <div className="container-x grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <span className="text-overline">Cómo funciona</span>
            <RevealTitle className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-charcoal mt-3 mb-6">
              {"Un proceso claro, tan tranquilo como tu viaje."}
            </RevealTitle>
            <p className="text-base text-warmgray leading-relaxed mb-8" data-reveal="fade">
              Recibirás un código único después de tu reserva en Yescapa.
              Con él, accedes al check-in guiado: datos personales, fotos del
              estado del vehículo, inventario y la firma del contrato. Al
              devolver la furgo, repetimos el proceso para liberar tu fianza.
            </p>
            <Button
              className="bg-forest-deep hover:bg-forest text-white"
              onClick={() =>
                window.open("https://www.yescapa.es/campers/91679", "_blank")
              }
              data-testid="landing-yescapa-btn"
              data-reveal="fade"
              style={{ "--i": 2 }}
            >
              Ver en Yescapa <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {[
              {
                icon: <Camera className="h-6 w-6" strokeWidth={1.5} />,
                title: "Fotos del vehículo",
                desc: "Documenta el estado antes y después del viaje. Sin sorpresas.",
              },
              {
                icon: <FileText className="h-6 w-6" strokeWidth={1.5} />,
                title: "Contrato firmado",
                desc: "Firma digital con validez legal según contrato Yescapa.",
              },
              {
                icon: <Shield className="h-6 w-6" strokeWidth={1.5} />,
                title: "Fianza segura",
                desc: "Pago de fianza con Stripe. Devolución tras el check-out.",
              },
              {
                icon: <MapPin className="h-6 w-6" strokeWidth={1.5} />,
                title: "Inventario completo",
                desc: "Lista verificada del equipamiento de la camper.",
              },
            ].map((f, i) => (
              <div
                key={f.title}
                data-reveal="fade"
                style={{ "--i": i }}
                className="bg-white border border-sand shadow-sm rounded-xl p-6 hover:-translate-y-1 hover:shadow-md transition-all duration-300"
              >
                <div className="h-10 w-10 rounded-lg bg-forest-deep text-white flex items-center justify-center mb-4">
                  {f.icon}
                </div>
                <h3 className="font-display font-semibold text-lg text-charcoal mb-1">
                  {f.title}
                </h3>
                <p className="text-sm text-warmgray leading-relaxed">
                  {f.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </AuroraSection>

      {/* CTA strip */}
      <AuroraSection variant="dark" className="py-20">
        <div className="container-x text-center text-white">
          <RevealTitle className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            ¿Listo para coger las llaves?
          </RevealTitle>
          <p className="text-white/95 max-w-xl mx-auto mb-8" data-reveal="fade">
            Introduce tu código de reserva arriba y completa el check-in en
            menos de 5 minutos.
          </p>
        </div>
      </AuroraSection>

      <footer className="py-10 container-x text-center text-sm text-warmgray-light">
        <div>
          VanCheck · Sistema de check-in para furgoneta camper Yescapa
        </div>
      </footer>
    </div>
  );
}
